/**
 * Price Scale Long Press Detector
 * Detects long press events on the price scale with drag threshold
 */

export interface PriceScaleLongPressOptions {
    longPressDuration: number // milliseconds
    dragThreshold: number // pixels
    onLongPress: () => void
}

export class PriceScaleLongPressDetector {
    private longPressTimer: NodeJS.Timeout | null = null
    private startX: number = 0
    private startY: number = 0
    private startTime: number = 0
    private isDragging: boolean = false
    private isLongPressTriggered: boolean = false
    private options: PriceScaleLongPressOptions

    constructor(options: PriceScaleLongPressOptions) {
        this.options = options
    }

    handleMouseDown = (event: MouseEvent | TouchEvent) => {
        const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0]?.clientX
        const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0]?.clientY

        if (clientX === undefined || clientY === undefined) {
            return
        }

        this.startX = clientX
        this.startY = clientY
        this.startTime = Date.now()
        this.isDragging = false
        this.isLongPressTriggered = false

        this.longPressTimer = setTimeout(() => {
            if (!this.isDragging && !this.isLongPressTriggered) {
                this.isLongPressTriggered = true
                this.options.onLongPress()
            }
        }, this.options.longPressDuration)

        document.addEventListener('mousemove', this.handleMouseMove)
        document.addEventListener('mouseup', this.handleMouseUp)
        document.addEventListener('touchmove', this.handleMouseMove, { passive: true })
        document.addEventListener('touchend', this.handleMouseUp)
    }

    handleMouseMove = (event: MouseEvent | TouchEvent) => {
        if (this.isLongPressTriggered) {
            return
        }

        const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0]?.clientX
        const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0]?.clientY

        if (clientX === undefined || clientY === undefined) {
            return
        }

        const deltaX = Math.abs(clientX - this.startX)
        const deltaY = Math.abs(clientY - this.startY)
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)

        if (distance > this.options.dragThreshold) {
            this.isDragging = true
            this.cancelLongPress()
        }
    }

    handleMouseUp = () => {
        this.cancelLongPress()
        this.cleanup()
    }

    private cancelLongPress() {
        if (this.longPressTimer) {
            clearTimeout(this.longPressTimer)
            this.longPressTimer = null
        }
    }

    private cleanup() {
        document.removeEventListener('mousemove', this.handleMouseMove)
        document.removeEventListener('mouseup', this.handleMouseUp)
        document.removeEventListener('touchmove', this.handleMouseMove)
        document.removeEventListener('touchend', this.handleMouseUp)
    }

    destroy() {
        this.cancelLongPress()
        this.cleanup()
    }
}
